import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../environments/environment';

export interface Language {
  code: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly apiUrl = 'https://translation.googleapis.com/language/translate/v2';
  private readonly apiKey = environment.googleMapsKey;
  
  public readonly supportedLanguages: Language[] = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'ar', name: 'Arabic' },
    { code: 'ur', name: 'Urdu' },
  ];

  private currentLanguage: string = 'en';
  private userChoseLanguage = false;
  private translationCache: Map<string, string> = new Map();

  // Maps ISO 3166-1 alpha-2 country codes to primary language codes
  private readonly countryLanguageMap: Record<string, string> = {
    // Spanish
    ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es',
    EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es',
    SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es',
    // French
    FR: 'fr', BE: 'fr', CH: 'fr', CA: 'fr', SN: 'fr', CI: 'fr', CM: 'fr',
    MG: 'fr', BF: 'fr', ML: 'fr', NE: 'fr', TD: 'fr', GN: 'fr',
    // German
    DE: 'de', AT: 'de', LI: 'de', LU: 'de',
    // Italian
    IT: 'it', SM: 'it', VA: 'it',
    // Portuguese
    PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt', CV: 'pt', GW: 'pt', ST: 'pt', TL: 'pt',
    // Chinese
    CN: 'zh', TW: 'zh', HK: 'zh', SG: 'zh', MO: 'zh',
    // Japanese
    JP: 'ja',
    // Korean
    KR: 'ko', KP: 'ko',
    // Arabic
    SA: 'ar', EG: 'ar', AE: 'ar', IQ: 'ar', MA: 'ar', DZ: 'ar', SD: 'ar',
    SY: 'ar', YE: 'ar', TN: 'ar', JO: 'ar', LY: 'ar', LB: 'ar', OM: 'ar',
    KW: 'ar', QA: 'ar', BH: 'ar', MR: 'ar', SO: 'ar', DJ: 'ar', KM: 'ar',
    // Urdu
    PK: 'ur',
  };

  constructor(private http: HttpClient) {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    const detectedLanguage = localStorage.getItem('detectedLanguage');
    console.log(`🌐 [TranslationService] Init — selectedLanguage: "${savedLanguage ?? 'none'}", detectedLanguage: "${detectedLanguage ?? 'none'}"`);

    if (savedLanguage) {
      // User manually chose a language — always respect it
      this.currentLanguage = savedLanguage;
      console.log(`🌐 [TranslationService] Using manually selected language: "${savedLanguage}"`);
    } else if (detectedLanguage) {
      // Previously auto-detected — use it instantly (no flash)
      this.currentLanguage = detectedLanguage;
      console.log(`🌐 [TranslationService] Using cached detected language: "${detectedLanguage}"`);
    } else {
      // First visit — use browser language. Do not request GPS here; the scan page
      // asks for location with a clear, user-tapped prompt so people are not surprised.
      this.currentLanguage = this.detectBrowserLanguage();
    }

    this.applyDocumentLanguage(this.currentLanguage);
  }

  private detectBrowserLanguage(): string {
    const browserLang = navigator.language.split('-')[0];
    const isSupported = this.supportedLanguages.some(lang => lang.code === browserLang);
    const resolved = isSupported ? browserLang : 'en';
    console.log(`🌐 [TranslationService] Browser language: "${navigator.language}" → resolved: "${resolved}"`);
    return resolved;
  }

  detectLanguageFromCoordinates(latitude: number, longitude: number): void {
    if (this.userChoseLanguage) {
      console.log('🌐 [TranslationService] Skipping location language — user picked a language.');
      return;
    }

    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&result_type=country&key=${this.apiKey}`;
    console.log(`🌐 [TranslationService] Resolving language from location: lat=${latitude}, lng=${longitude}`);

    this.http.get<any>(geocodeUrl)
      .pipe(catchError((err) => {
        console.error('🌐 [TranslationService] Geocoding API error:', err);
        return of(null);
      }))
      .subscribe(data => {
        if (!data?.results?.length) {
          console.warn('🌐 [TranslationService] No geocoding results returned.');
          return;
        }

        const countryComponent = data.results[0]?.address_components
          ?.find((c: any) => c.types.includes('country'));
        const countryCode = countryComponent?.short_name;
        const langCode = countryCode ? this.countryLanguageMap[countryCode] : undefined;
        console.log(`🌐 [TranslationService] Detected country: "${countryCode}" → language: "${langCode ?? 'none'}"`);

        if (langCode) {
          this.applyDetectedLanguage(langCode);
        }
      });
  }

  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  setCurrentLanguage(languageCode: string): void {
    this.userChoseLanguage = true;
    this.currentLanguage = languageCode;
    localStorage.setItem('selectedLanguage', languageCode);
    localStorage.setItem('detectedLanguage', languageCode);
    this.applyDocumentLanguage(languageCode);
  }

  applyDetectedLanguage(languageCode: string): void {
    localStorage.removeItem('selectedLanguage');
    localStorage.setItem('detectedLanguage', languageCode);
    if (languageCode === this.currentLanguage) {
      return;
    }

    console.log(`🌐 [TranslationService] Applying detected language: "${this.currentLanguage}" → "${languageCode}"`);
    this.currentLanguage = languageCode;
    this.applyDocumentLanguage(languageCode);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('languageChanged', {
        detail: { languageCode }
      }));
    }, 0);
  }

  applyDefaultEnglish(): void {
    this.currentLanguage = 'en';
    this.applyDocumentLanguage('en');
  }

  applyDocumentLanguage(languageCode: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    const rtlLanguages = ['ar', 'ur'];
    const dir = rtlLanguages.includes(languageCode) ? 'rtl' : 'ltr';
    document.documentElement.lang = languageCode;
    document.documentElement.dir = dir;
    document.documentElement.classList.toggle('lang-ur', languageCode === 'ur');
    if (document.body) {
      document.body.dir = dir;
    }
  }

  translate(text: string, targetLanguage?: string): Observable<string> {
    if (!text || !text.trim()) {
      return of(text);
    }

    const target = targetLanguage || this.currentLanguage;
    
    if (target === 'en') {
      return of(text);
    }

    const cacheKey = `${text}:${target}`;
    if (this.translationCache.has(cacheKey)) {
      return of(this.translationCache.get(cacheKey)!);
    }

    const params = new HttpParams()
      .set('key', this.apiKey)
      .set('q', text)
      .set('target', target)
      .set('format', 'text');

    return this.http.post<any>(this.apiUrl, null, { params }).pipe(
      map((response) => {
        if (response?.data?.translations && response.data.translations.length > 0) {
          const translatedText = response.data.translations[0].translatedText;
          this.translationCache.set(cacheKey, translatedText);
          return translatedText;
        }
        return text;
      }),
      catchError(() => of(text))
    );
  }

  translateBatch(texts: string[], targetLanguage?: string): Observable<string[]> {
    const target = targetLanguage || this.currentLanguage;
    
    if (target === 'en') {
      return of(texts);
    }

    if (texts.length === 0) {
      return of([]);
    }

    const textsToTranslate: string[] = [];
    const cachedResults: (string | null)[] = [];
    const indices: number[] = [];

    texts.forEach((text, index) => {
      if (!text || !text.trim()) {
        cachedResults[index] = text;
      } else {
        const cacheKey = `${text}:${target}`;
        if (this.translationCache.has(cacheKey)) {
          cachedResults[index] = this.translationCache.get(cacheKey)!;
        } else {
          textsToTranslate.push(text);
          indices.push(index);
        }
      }
    });

    if (textsToTranslate.length === 0) {
      return of(cachedResults.map((result, index) => result !== null ? result : texts[index]));
    }

    let params = new HttpParams()
      .set('key', this.apiKey)
      .set('target', target)
      .set('format', 'text');

    textsToTranslate.forEach(text => {
      params = params.append('q', text);
    });

    return this.http.post<any>(this.apiUrl, null, { params }).pipe(
      map((response) => {
        if (response?.data?.translations) {
          const translations = response.data.translations;
          
          translations.forEach((translation: any, i: number) => {
            const originalText = textsToTranslate[i];
            const translatedText = translation.translatedText;
            const cacheKey = `${originalText}:${target}`;
            this.translationCache.set(cacheKey, translatedText);
            cachedResults[indices[i]] = translatedText;
          });
        }

        return cachedResults.map((result, index) => result !== null ? result : texts[index]);
      }),
      catchError(() => of(texts))
    );
  }

  clearCache(): void {
    this.translationCache.clear();
  }
}

